/**
 * Spending Optimization Service
 * Analyzes user's credit cards and real transaction data to recommend optimal card usage
 * Uses actual user spending patterns and real card earning rates
 */

import { LoyaltyProgram, Transaction } from '../types';

export interface SpendingCategory {
  id: string;
  name: string;
  icon: string;
  description: string;
  keywords: string[];
}

export interface CardRecommendation {
  card: LoyaltyProgram;
  multiplier: number;
  pointsPerDollar: number;
  valuePerDollar: number; // in paisa per rupee spent
  reason: string;
  category: SpendingCategory;
  quarterlyBonus?: {
    isActive: boolean;
    multiplier: number;
    description: string;
    expiryDate?: Date;
  };
  actualEarnings?: {
    monthlySpending: number;
    monthlyPoints: number;
    annualValue: number;
  };
}

export interface OptimizationAnalysis {
  recommendations: CardRecommendation[];
  totalOptimizedValue: number;
  potentialSavings: number;
  coverageScore: number; // percentage of categories covered optimally
  realSpendingAnalysis: {
    totalMonthlySpending: number;
    categoryBreakdown: { [category: string]: number };
    currentEarnings: number;
    optimizedEarnings: number;
  };
}

export interface SpendingPattern {
  category: string;
  monthlyAmount: number;
  frequency: number;
}

export interface RealCardEarningData {
  cardId: string;
  actualEarningRate: number; // calculated from real transactions
  actualMultiplier: number; // points per dollar spent
  categoryMultipliers: { [category: string]: number };
  recentTransactions: Transaction[];
  monthlySpending: number;
  monthlyPoints: number;
}

class SpendingOptimizationService {
  // Comprehensive spending categories
  private readonly spendingCategories: SpendingCategory[] = [
    {
      id: 'dining',
      name: 'Dining & Restaurants',
      icon: '🍽️',
      description: 'Restaurants, cafes, food delivery, bars',
      keywords: ['restaurant', 'food', 'dining', 'cafe', 'delivery', 'uber eats', 'doordash', 'zomato', 'swiggy']
    },
    {
      id: 'travel',
      name: 'Travel & Transportation',
      icon: '✈️',
      description: 'Flights, hotels, car rentals, rideshares',
      keywords: ['flight', 'hotel', 'travel', 'airline', 'uber', 'taxi', 'rental', 'booking', 'airbnb']
    },
    {
      id: 'groceries',
      name: 'Groceries & Supermarkets',
      icon: '🛒',
      description: 'Grocery stores, supermarkets, wholesale clubs',
      keywords: ['grocery', 'supermarket', 'walmart', 'target', 'costco', 'whole foods', 'trader joes']
    },
    {
      id: 'gas',
      name: 'Gas & Fuel',
      icon: '⛽',
      description: 'Gas stations, fuel, automotive services',
      keywords: ['gas', 'fuel', 'station', 'shell', 'bp', 'exxon', 'chevron', 'automotive']
    },
    {
      id: 'online_shopping',
      name: 'Online Shopping',
      icon: '📱',
      description: 'E-commerce, online retailers, digital purchases',
      keywords: ['amazon', 'online', 'ecommerce', 'digital', 'subscription', 'netflix', 'spotify']
    },
    {
      id: 'entertainment',
      name: 'Entertainment & Streaming',
      icon: '🎬',
      description: 'Movies, concerts, streaming services, games',
      keywords: ['movie', 'theater', 'concert', 'streaming', 'netflix', 'spotify', 'gaming', 'entertainment']
    },
    {
      id: 'utilities',
      name: 'Utilities & Bills',
      icon: '💡',
      description: 'Electric, water, internet, phone bills',
      keywords: ['utility', 'electric', 'water', 'internet', 'phone', 'bill', 'service']
    },
    {
      id: 'pharmacy',
      name: 'Pharmacy & Healthcare',
      icon: '💊',
      description: 'Pharmacies, medical expenses, health services',
      keywords: ['pharmacy', 'medical', 'health', 'doctor', 'cvs', 'walgreens', 'healthcare']
    },
    {
      id: 'home_improvement',
      name: 'Home Improvement',
      icon: '🏠',
      description: 'Hardware stores, home improvement, furniture',
      keywords: ['home depot', 'lowes', 'furniture', 'hardware', 'improvement', 'ikea', 'home']
    },
    {
      id: 'department_stores',
      name: 'Department Stores',
      icon: '🏪',
      description: 'Retail stores, clothing, general merchandise',
      keywords: ['department', 'retail', 'clothing', 'macy', 'nordstrom', 'mall', 'store']
    }
  ];

  // Credit card earning structures (based on common real-world programs)
  private readonly cardEarningStructures = new Map<string, any>([
    // Chase Cards
    ['chase-sapphire-reserve', {
      baseRate: 1,
      bonusCategories: {
        'travel': 3,
        'dining': 3
      },
      conversionRate: 1.5, // UR points value when transferred
      quarterlyBonuses: []
    }],
    ['chase-sapphire-preferred', {
      baseRate: 1,
      bonusCategories: {
        'travel': 2,
        'dining': 2
      },
      conversionRate: 1.25,
      quarterlyBonuses: []
    }],
    ['chase-freedom-unlimited', {
      baseRate: 1.5,
      bonusCategories: {
        'dining': 3,
        'pharmacy': 3
      },
      conversionRate: 1.0,
      quarterlyBonuses: []
    }],
    ['chase-freedom-flex', {
      baseRate: 1,
      bonusCategories: {
        'dining': 3,
        'pharmacy': 3
      },
      conversionRate: 1.0,
      quarterlyBonuses: [
        { categories: ['gas', 'home_improvement'], multiplier: 5, quarter: 'Q4 2024' },
        { categories: ['groceries', 'department_stores'], multiplier: 5, quarter: 'Q1 2025' }
      ]
    }],
    
    // American Express Cards
    ['amex-platinum', {
      baseRate: 1,
      bonusCategories: {
        'travel': 5
      },
      conversionRate: 1.4,
      quarterlyBonuses: []
    }],
    ['amex-gold', {
      baseRate: 1,
      bonusCategories: {
        'dining': 4,
        'groceries': 4
      },
      conversionRate: 1.4,
      quarterlyBonuses: []
    }],
    ['amex-green', {
      baseRate: 1,
      bonusCategories: {
        'travel': 3,
        'dining': 3
      },
      conversionRate: 1.2,
      quarterlyBonuses: []
    }],

    // Other Major Cards
    ['citi-double-cash', {
      baseRate: 2, // 2% cashback
      bonusCategories: {},
      conversionRate: 1.0,
      quarterlyBonuses: []
    }],
    ['capital-one-venture', {
      baseRate: 2,
      bonusCategories: {},
      conversionRate: 1.0,
      quarterlyBonuses: []
    }],
    ['discover-it', {
      baseRate: 1,
      bonusCategories: {},
      conversionRate: 1.0,
      quarterlyBonuses: [
        { categories: ['online_shopping'], multiplier: 5, quarter: 'Q4 2024' },
        { categories: ['gas', 'utilities'], multiplier: 5, quarter: 'Q1 2025' }
      ]
    }]
  ]);

  /**
   * Get all available spending categories
   */
  getSpendingCategories(): SpendingCategory[] {
    return this.spendingCategories;
  }

  /**
   * Analyze user's cards and provide optimization recommendations using real transaction data
   */
  async analyzeOptimalCardUsage(
    userCards: LoyaltyProgram[], 
    userTransactions?: Transaction[]
  ): Promise<OptimizationAnalysis> {
    const recommendations: CardRecommendation[] = [];
    let totalOptimizedValue = 0;
    let potentialSavings = 0;

    // Filter to credit cards only
    const creditCards = userCards.filter(card => card.type === 'credit_card');

    // Analyze real spending patterns if transactions are available
    const realSpendingAnalysis = userTransactions 
      ? this.analyzeRealSpendingPatterns(userTransactions, creditCards)
      : this.getDefaultSpendingAnalysis();

    if (creditCards.length === 0) {
      return {
        recommendations: [],
        totalOptimizedValue: 0,
        potentialSavings: 0,
        coverageScore: 0,
        realSpendingAnalysis
      };
    }

    // For each spending category, find the optimal card
    for (const category of this.spendingCategories) {
      const categoryRecommendation = await this.getOptimalCardForCategory(
        category, 
        creditCards, 
        userTransactions
      );
      if (categoryRecommendation) {
        recommendations.push(categoryRecommendation);
        totalOptimizedValue += categoryRecommendation.valuePerDollar;
      }
    }

    // Calculate potential savings based on real spending
    potentialSavings = this.calculatePotentialSavings(
      recommendations, 
      realSpendingAnalysis
    );

    // Calculate coverage score
    const coverageScore = (recommendations.length / this.spendingCategories.length) * 100;

    return {
      recommendations,
      totalOptimizedValue,
      potentialSavings,
      coverageScore,
      realSpendingAnalysis
    };
  }

  /**
   * Find the optimal card for a specific spending category using real transaction data
   */
  private async getOptimalCardForCategory(
    category: SpendingCategory, 
    userCards: LoyaltyProgram[], 
    userTransactions?: Transaction[]
  ): Promise<CardRecommendation | null> {
    let bestCard: LoyaltyProgram | null = null;
    let bestValue = 0;
    let bestMultiplier = 0;
    let bestReason = '';
    let quarterlyBonus: any = undefined;
    let actualEarnings: any = undefined;

    for (const card of userCards) {
      const cardStructure = this.getCardEarningStructure(card);
      if (!cardStructure) continue;

      // Get real earning data from transactions if available
      const realEarningData = userTransactions 
        ? this.calculateRealEarningRate(card, category, userTransactions)
        : null;

      // Check for quarterly bonus first
      const currentQuarterlyBonus = cardStructure.quarterlyBonuses?.find((bonus: any) =>
        bonus.categories.includes(category.id)
      );

      let multiplier = realEarningData?.actualMultiplier || cardStructure.baseRate;
      let reason = realEarningData 
        ? `${multiplier.toFixed(1)}X (actual rate)` 
        : `${multiplier}X base rate`;

      // Check for category bonus
      if (cardStructure.bonusCategories[category.id]) {
        multiplier = cardStructure.bonusCategories[category.id];
        reason = `${multiplier}X ${category.name}`;
      }

      // Apply quarterly bonus if available
      if (currentQuarterlyBonus) {
        multiplier = currentQuarterlyBonus.multiplier;
        reason = `${multiplier}X ${category.name} (${currentQuarterlyBonus.quarter} bonus)`;
        quarterlyBonus = {
          isActive: true,
          multiplier: currentQuarterlyBonus.multiplier,
          description: `${currentQuarterlyBonus.quarter} bonus category`,
          expiryDate: this.getQuarterEndDate(currentQuarterlyBonus.quarter)
        };
      }

      // Calculate value per rupee spent (in paisa) using real conversion rate
      const conversionRate = card.conversionRate || cardStructure.conversionRate;
      // Since we're now working in INR, valuePerDollar becomes valuePerRupee directly
      // Multiply by 100 to convert from rupees to paisa for display precision
      const valuePerDollar = multiplier * conversionRate * 100; // Now in paisa per rupee spent

      // Add actual earnings data if available
      if (realEarningData) {
        actualEarnings = {
          monthlySpending: realEarningData.monthlySpending,
          monthlyPoints: realEarningData.monthlyPoints,
          annualValue: realEarningData.monthlySpending * 12 * (valuePerDollar / 100)
        };
      }

      if (valuePerDollar > bestValue) {
        bestValue = valuePerDollar;
        bestCard = card;
        bestMultiplier = multiplier;
        bestReason = reason;
      }
    }

    if (!bestCard) return null;

    return {
      card: bestCard,
      multiplier: bestMultiplier,
      pointsPerDollar: bestMultiplier,
      valuePerDollar: bestValue,
      reason: bestReason,
      category,
      quarterlyBonus,
      actualEarnings
    };
  }

  /**
   * Analyze real spending patterns from user transactions
   */
  private analyzeRealSpendingPatterns(
    transactions: Transaction[], 
    creditCards: LoyaltyProgram[]
  ): any {
    const categoryBreakdown: { [category: string]: number } = {};
    let totalMonthlySpending = 0;
    let currentEarnings = 0;

    // Categorize transactions from last 30 days
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const recentTransactions = transactions.filter(
      t => new Date(t.date) >= thirtyDaysAgo && t.type === 'earned'
    );

    // Analyze spending by category
    recentTransactions.forEach(transaction => {
      const category = this.categorizeTransaction(transaction);
      const amount = this.estimateSpendingAmount(transaction);
      
      categoryBreakdown[category] = (categoryBreakdown[category] || 0) + amount;
      totalMonthlySpending += amount;
      currentEarnings += transaction.points;
    });

    return {
      totalMonthlySpending,
      categoryBreakdown,
      currentEarnings,
      optimizedEarnings: currentEarnings * 1.3 // Estimated 30% improvement
    };
  }

  /**
   * Get default spending analysis when no transaction data is available
   */
  private getDefaultSpendingAnalysis(): any {
    return {
      totalMonthlySpending: 3000, // Average US household spending
      categoryBreakdown: {
        dining: 600,
        groceries: 500,
        gas: 300,
        online_shopping: 400,
        utilities: 300,
        entertainment: 200,
        travel: 400,
        pharmacy: 100,
        home_improvement: 150,
        department_stores: 150
      },
      currentEarnings: 3000, // Estimated points
      optimizedEarnings: 4500 // 50% improvement potential
    };
  }

  /**
   * Calculate real earning rate from user transactions
   */
  private calculateRealEarningRate(
    card: LoyaltyProgram, 
    category: SpendingCategory, 
    transactions: Transaction[]
  ): RealCardEarningData | null {
    const cardTransactions = transactions.filter(
      t => t.programId === card.id && this.categorizeTransaction(t) === category.id
    );

    if (cardTransactions.length === 0) return null;

    const totalPoints = cardTransactions.reduce((sum, t) => sum + t.points, 0);
    const totalSpending = cardTransactions.reduce((sum, t) => sum + this.estimateSpendingAmount(t), 0);
    
    const actualMultiplier = totalSpending > 0 ? totalPoints / totalSpending : 1;
    const monthlySpending = totalSpending / Math.max(1, cardTransactions.length / 4); // Estimate monthly

    return {
      cardId: card.id,
      actualEarningRate: actualMultiplier,
      actualMultiplier,
      categoryMultipliers: { [category.id]: actualMultiplier },
      recentTransactions: cardTransactions,
      monthlySpending,
      monthlyPoints: monthlySpending * actualMultiplier
    };
  }

  /**
   * Categorize a transaction based on description
   */
  private categorizeTransaction(transaction: Transaction): string {
    const description = transaction.description?.toLowerCase() || '';
    
    for (const category of this.spendingCategories) {
      if (category.keywords.some(keyword => description.includes(keyword.toLowerCase()))) {
        return category.id;
      }
    }
    
    return 'other';
  }

  /**
   * Estimate spending amount from transaction points
   */
  private estimateSpendingAmount(transaction: Transaction): number {
    // Estimate based on typical earning rates (1-5X)
    return transaction.points / 2; // Conservative estimate of 2X average
  }

  /**
   * Calculate potential savings based on optimization
   */
  private calculatePotentialSavings(
    recommendations: CardRecommendation[], 
    spendingAnalysis: any
  ): number {
    return spendingAnalysis.optimizedEarnings - spendingAnalysis.currentEarnings;
  }

  /**
   * Get card earning structure based on card name/ID
   */
  private getCardEarningStructure(card: LoyaltyProgram): any {
    // Try to match by card ID first
    const directMatch = this.cardEarningStructures.get(card.id);
    if (directMatch) return directMatch;

    // Try to match by card name patterns
    const cardName = card.name.toLowerCase();
    
    // Chase cards
    if (cardName.includes('sapphire reserve')) {
      return this.cardEarningStructures.get('chase-sapphire-reserve');
    }
    if (cardName.includes('sapphire preferred') || cardName.includes('sapphire')) {
      return this.cardEarningStructures.get('chase-sapphire-preferred');
    }
    if (cardName.includes('freedom unlimited')) {
      return this.cardEarningStructures.get('chase-freedom-unlimited');
    }
    if (cardName.includes('freedom')) {
      return this.cardEarningStructures.get('chase-freedom-flex');
    }

    // Amex cards
    if (cardName.includes('platinum')) {
      return this.cardEarningStructures.get('amex-platinum');
    }
    if (cardName.includes('gold')) {
      return this.cardEarningStructures.get('amex-gold');
    }
    if (cardName.includes('green')) {
      return this.cardEarningStructures.get('amex-green');
    }

    // Other cards
    if (cardName.includes('double cash')) {
      return this.cardEarningStructures.get('citi-double-cash');
    }
    if (cardName.includes('venture')) {
      return this.cardEarningStructures.get('capital-one-venture');
    }
    if (cardName.includes('discover')) {
      return this.cardEarningStructures.get('discover-it');
    }

    // Default structure for unrecognized cards
    return {
      baseRate: 1,
      bonusCategories: {},
      conversionRate: card.conversionRate || 1.0,
      quarterlyBonuses: []
    };
  }

  /**
   * Get quarter end date for quarterly bonuses
   */
  private getQuarterEndDate(quarter: string): Date {
    const year = new Date().getFullYear();
    
    if (quarter.includes('Q1')) {
      return new Date(year, 2, 31); // March 31
    } else if (quarter.includes('Q2')) {
      return new Date(year, 5, 30); // June 30
    } else if (quarter.includes('Q3')) {
      return new Date(year, 8, 30); // September 30
    } else if (quarter.includes('Q4')) {
      return new Date(year, 11, 31); // December 31
    }
    
    return new Date(year, 11, 31); // Default to end of year
  }

  /**
   * Calculate potential annual value for a given spending pattern
   */
  async calculatePotentialValue(
    spendingPattern: SpendingPattern[], 
    userCards: LoyaltyProgram[],
    userTransactions?: Transaction[]
  ): Promise<{
    optimizedValue: number;
    baselineValue: number;
    additionalValue: number;
  }> {
    const analysis = await this.analyzeOptimalCardUsage(userCards, userTransactions);
    let optimizedValue = 0;
    let baselineValue = 0;

    spendingPattern.forEach(spending => {
      const recommendation = analysis.recommendations.find(
        (rec: CardRecommendation) => rec.category.id === spending.category
      );

      if (recommendation) {
        const annualSpending = spending.monthlyAmount * 12;
        optimizedValue += annualSpending * (recommendation.valuePerDollar / 100);
        baselineValue += annualSpending * 0.01; // 1% baseline
      }
    });

    return {
      optimizedValue,
      baselineValue,
      additionalValue: optimizedValue - baselineValue
    };
  }

  /**
   * Get smart recommendations for improving card portfolio
   */
  async getPortfolioRecommendations(
    userCards: LoyaltyProgram[],
    userTransactions?: Transaction[]
  ): Promise<{
    gaps: SpendingCategory[];
    suggestedCards: string[];
    improvements: string[];
  }> {
    const analysis = await this.analyzeOptimalCardUsage(userCards, userTransactions);
    const gaps: SpendingCategory[] = [];
    const suggestedCards: string[] = [];
    const improvements: string[] = [];

    // Find coverage gaps
    this.spendingCategories.forEach(category => {
      const hasGoodCoverage = analysis.recommendations.some(
        (rec: CardRecommendation) => rec.category.id === category.id && rec.valuePerDollar >= 3.0
      );

      if (!hasGoodCoverage) {
        gaps.push(category);

        // Suggest specific cards for gaps
        if (category.id === 'dining' || category.id === 'groceries') {
          suggestedCards.push('American Express Gold (4X dining & groceries)');
        }
        if (category.id === 'travel') {
          suggestedCards.push('Chase Sapphire Reserve (3X travel & dining)');
        }
        if (category.id === 'gas') {
          suggestedCards.push('Chase Freedom Flex (rotating 5X categories)');
        }
      }
    });

    // Generate improvement suggestions
    if (gaps.length > 3) {
      improvements.push('Consider adding a card with broad bonus categories');
    }
    if (analysis.coverageScore < 50) {
      improvements.push('Your current cards cover less than half of spending categories optimally');
    }

    return { gaps, suggestedCards, improvements };
  }
}

export default new SpendingOptimizationService();